import React, { useEffect, useRef, useState } from 'react';
import './PdpMobileImageLightbox.css';

const MIN_SCALE = 1;
const MAX_SCALE = 5;
/** Double-tap zoom (~450% — roughly 2× the previous 225% level) */
const DOUBLE_TAP_SCALE = 4.5;
const SWIPE_MIN_PX = 52;

function touchDistance(a, b) {
  return Math.hypot(b.clientX - a.clientX, b.clientY - a.clientY);
}

function clamp(n, min, max) {
  return Math.min(max, Math.max(min, n));
}

/**
 * Mobile-only full-screen PDP gallery (separate from desktop image-modal).
 * Pinch to zoom + drag when zoomed; swipe between photos at 1× scale.
 */
export default function PdpMobileImageLightbox({
  productName,
  slideCount,
  currentIndex,
  onClose,
  onSelectIndex,
  getImageUrl,
}) {
  const [view, setView] = useState({ scale: 1, x: 0, y: 0 });
  const [gesturing, setGesturing] = useState(false);
  const stageRef = useRef(null);
  const viewRef = useRef(view);
  const gestureRef = useRef(null);
  const filmstripRef = useRef(null);
  const lastTapRef = useRef({ time: 0, x: 0, y: 0 });

  viewRef.current = view;
  const isZoomed = view.scale > 1.02;

  useEffect(() => {
    document.body.classList.add('pdp-mobile-lightbox-open');
    return () => document.body.classList.remove('pdp-mobile-lightbox-open');
  }, []);

  useEffect(() => {
    setView({ scale: 1, x: 0, y: 0 });
  }, [currentIndex]);

  useEffect(() => {
    const strip = filmstripRef.current;
    if (!strip) return;
    const active = strip.querySelector('.pdp-m-lightbox__thumb.is-active');
    if (active?.scrollIntoView) {
      active.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
    }
  }, [currentIndex]);

  useEffect(() => {
    const el = stageRef.current;
    if (!el) return undefined;

    const onTouchStart = (e) => {
      if (e.touches.length === 2) {
        const d = touchDistance(e.touches[0], e.touches[1]);
        gestureRef.current = {
          mode: 'pinch',
          startDist: d,
          startScale: viewRef.current.scale,
        };
        return;
      }
      if (e.touches.length === 1) {
        const t = e.touches[0];
        if (viewRef.current.scale > 1.02) {
          gestureRef.current = {
            mode: 'pan',
            startX: t.clientX,
            startY: t.clientY,
            baseX: viewRef.current.x,
            baseY: viewRef.current.y,
          };
        } else {
          gestureRef.current = {
            mode: 'swipe',
            x: t.clientX,
            y: t.clientY,
          };
        }
      }
    };

    const onTouchMove = (e) => {
      const g = gestureRef.current;
      if (!g) return;

      if (g.mode === 'pinch' && e.touches.length >= 2) {
        e.preventDefault();
        setGesturing(true);
        const d = touchDistance(e.touches[0], e.touches[1]);
        if (!g.startDist) return;
        const nextScale = clamp(g.startScale * (d / g.startDist), MIN_SCALE, MAX_SCALE);
        setView((v) => ({
          ...v,
          scale: nextScale,
          x: nextScale <= 1.02 ? 0 : v.x,
          y: nextScale <= 1.02 ? 0 : v.y,
        }));
        return;
      }

      if (g.mode === 'pan' && e.touches.length === 1) {
        e.preventDefault();
        setGesturing(true);
        const t = e.touches[0];
        setView((v) => ({
          ...v,
          x: g.baseX + (t.clientX - g.startX),
          y: g.baseY + (t.clientY - g.startY),
        }));
      }
    };

    const onTouchEnd = (e) => {
      const g = gestureRef.current;

      if (e.touches.length === 1 && viewRef.current.scale > 1.02) {
        const t = e.touches[0];
        gestureRef.current = {
          mode: 'pan',
          startX: t.clientX,
          startY: t.clientY,
          baseX: viewRef.current.x,
          baseY: viewRef.current.y,
        };
        return;
      }

      if (e.touches.length > 0) return;

      setGesturing(false);
      gestureRef.current = null;

      if (viewRef.current.scale < 1.05) {
        setView({ scale: 1, x: 0, y: 0 });
      }

      if (!e.changedTouches?.length) return;
      const end = e.changedTouches[0];

      let startX = end.clientX;
      let startY = end.clientY;
      if (g?.mode === 'swipe') {
        startX = g.x;
        startY = g.y;
      } else if (g?.mode === 'pan') {
        startX = g.startX;
        startY = g.startY;
      }

      const absH = Math.abs(startX - end.clientX);
      const absV = Math.abs(startY - end.clientY);
      const isTap = absH < 14 && absV < 14;

      if (isTap) {
        const now = Date.now();
        const last = lastTapRef.current;
        const isDoubleTap =
          now - last.time < 360 &&
          Math.hypot(end.clientX - last.x, end.clientY - last.y) < 32;
        if (isDoubleTap) {
          if (viewRef.current.scale > 1.02) {
            setView({ scale: 1, x: 0, y: 0 });
          } else {
            setView({ scale: DOUBLE_TAP_SCALE, x: 0, y: 0 });
          }
          lastTapRef.current = { time: 0, x: 0, y: 0 };
          return;
        }
        lastTapRef.current = { time: now, x: end.clientX, y: end.clientY };
        return;
      }

      if (viewRef.current.scale > 1.02) return;
      if (!g || g.mode !== 'swipe' || slideCount <= 1) return;

      if (absH < SWIPE_MIN_PX || absH <= absV * 1.3) return;
      if (g.x - end.clientX > 0) {
        onSelectIndex((currentIndex + 1) % slideCount);
      } else {
        onSelectIndex((currentIndex - 1 + slideCount) % slideCount);
      }
    };

    const onCancel = () => {
      gestureRef.current = null;
      setGesturing(false);
    };

    el.addEventListener('touchstart', onTouchStart, { passive: true });
    el.addEventListener('touchmove', onTouchMove, { passive: false });
    el.addEventListener('touchend', onTouchEnd, { passive: true });
    el.addEventListener('touchcancel', onCancel, { passive: true });
    return () => {
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove', onTouchMove);
      el.removeEventListener('touchend', onTouchEnd);
      el.removeEventListener('touchcancel', onCancel);
    };
  }, [slideCount, currentIndex, onSelectIndex]);

  const mainUrl = getImageUrl(currentIndex) || getImageUrl(0);

  const goPrev = () => {
    if (slideCount <= 1 || isZoomed) return;
    onSelectIndex((currentIndex - 1 + slideCount) % slideCount);
  };

  const goNext = () => {
    if (slideCount <= 1 || isZoomed) return;
    onSelectIndex((currentIndex + 1) % slideCount);
  };

  const photoStyle = {
    transform: `translate3d(${view.x}px, ${view.y}px, 0) scale(${view.scale})`,
    transition: gesturing ? 'none' : 'transform 0.22s ease-out',
  };

  return (
    <div
      className="pdp-m-lightbox"
      role="dialog"
      aria-modal="true"
      aria-label={productName ? `${productName} photos` : 'Product photos'}
    >
      <header className="pdp-m-lightbox__top">
        <button type="button" className="pdp-m-lightbox__done" onClick={onClose}>
          Done
        </button>
        {slideCount > 1 ? (
          <span className="pdp-m-lightbox__pill">
            {currentIndex + 1} / {slideCount}
          </span>
        ) : (
          <span className="pdp-m-lightbox__pill pdp-m-lightbox__pill--solo">Photo</span>
        )}
        <button
          type="button"
          className="pdp-m-lightbox__icon-close"
          onClick={onClose}
          aria-label="Close gallery"
        >
          <i className="fas fa-times" aria-hidden />
        </button>
      </header>

      <div className="pdp-m-lightbox__stage" ref={stageRef}>
        <div className="pdp-m-lightbox__viewport">
          {mainUrl ? (
            <img
              key={currentIndex}
              src={mainUrl}
              alt={
                productName
                  ? `${productName} — photo ${currentIndex + 1}`
                  : `Product photo ${currentIndex + 1}`
              }
              className="pdp-m-lightbox__photo"
              style={photoStyle}
              draggable={false}
            />
          ) : (
            <div className="pdp-m-lightbox__empty">No image</div>
          )}
        </div>
      </div>

      {slideCount > 1 && !isZoomed && (
        <div className="pdp-m-lightbox__dots" aria-hidden>
          {Array.from({ length: slideCount }, (_, i) => (
            <span
              key={i}
              className={`pdp-m-lightbox__dot${i === currentIndex ? ' is-active' : ''}`}
            />
          ))}
        </div>
      )}

      {slideCount > 1 && (
        <footer className={`pdp-m-lightbox__filmstrip${isZoomed ? ' is-dimmed' : ''}`}>
          <div className="pdp-m-lightbox__filmstrip-scroll" ref={filmstripRef}>
            {Array.from({ length: slideCount }, (_, index) => {
              const thumbSrc = getImageUrl(index);
              if (!thumbSrc) return null;
              return (
                <button
                  key={index}
                  type="button"
                  className={`pdp-m-lightbox__thumb${index === currentIndex ? ' is-active' : ''}`}
                  onClick={() => onSelectIndex(index)}
                  aria-label={`View photo ${index + 1}`}
                  aria-current={index === currentIndex ? 'true' : undefined}
                >
                  <img src={thumbSrc} alt="" draggable={false} />
                </button>
              );
            })}
          </div>
        </footer>
      )}

      {slideCount > 1 && !isZoomed && (
        <div className="pdp-m-lightbox__edge-nav" aria-hidden>
          <button type="button" className="pdp-m-lightbox__edge pdp-m-lightbox__edge--prev" onClick={goPrev} tabIndex={-1} />
          <button type="button" className="pdp-m-lightbox__edge pdp-m-lightbox__edge--next" onClick={goNext} tabIndex={-1} />
        </div>
      )}
    </div>
  );
}
